import { faSparkles } from "@fortawesome/pro-solid-svg-icons";
import type { Editor, JSONContent } from "@tiptap/vue-3";
import type { Form } from "~/models/Form";
import type ApiError from "~/plugins/errors/apiError";
import type {
    MailTemplate,
    MailTemplateTranslation,
} from "~/services/MailTemplateService";
import type { ApiResponse } from "~/types/ApiResponse";
import {
    faSignature,
    faObjectGroup,
    faColumns,
    faAlignJustify,
    faParagraph,
    faHandPointer,
    faHandPointRight,
    faQuoteRight,
    faListOl,
    faListUl,
    faImage,
    faImages,
    faFilm,
    faFileAlt,
    faHourglassHalf,
    faHeader,
} from "@fortawesome/pro-light-svg-icons";
import { faHtml5 } from "@fortawesome/free-brands-svg-icons";
import { faShareNodes } from "@fortawesome/pro-regular-svg-icons";

const EMPTY_MAIL: MailTemplate = {
    id: createUuid(),
    brand: { id: null },
    form: { id: null },
    form_field: { id: null },
    locale: { id: null },
    is_template: false,
    is_active_template: false,
    auto_translate: false,
    subject: "",
    type: "mjml",
    name: "",
    description: "",
    translations: [],
    metadata: {
        tags: [],
        fields: [],
        utm_campaign: {
            campaign: null,
            source: null,
            medium: null,
            content: null,
            term: null,
        },
    },
    text_data: null,
    json_data: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    preview_url: "",
};

export interface MailBuilderConfig {
    showValues: boolean;
    previewMinimized: boolean;
    codeEditorVisible: boolean;
    previewSize: "desktop" | "tablet" | "mobile";
}

export interface TestMailOptions {
    data?: Record<string, any> | object;
    to_first_name?: string;
    to_last_name?: string;
    to_email?: string;
    automation_id?: string;
    brand_id?: string;
    form_id?: string;
    form_field_id?: string;
    step_id?: string;
    locale_id?: number;
}

export const mailBlockTypeIcons = {
    document: [faFileAlt, "text-red-500"],
    signature: [faSignature, "text-purple-600"],
    section: [faObjectGroup, "text-yellow-500"],
    layout: [faColumns, "text-green-500"],
    column: [faAlignJustify, "text-blue-500"],
    columns: [faAlignJustify, "text-blue-500"],
    paragraph: [faParagraph, "text-indigo-500"],
    button: [faHandPointer, "text-purple-500"],
    "button-group": [faHandPointRight, "text-pink-500"],
    blockquote: [faQuoteRight, "text-red-600"],
    heading: [faHeader, "text-yellow-600"],
    form: [faFileAlt, "text-gray-900"],
    "number-list": [faListOl, "text-red-700"],
    "bullet-list": [faListUl, "text-yellow-700"],
    image: [faImage, "text-green-700"],
    gallery: [faImages, "text-blue-700"],
    video: [faFilm, "text-indigo-700"],
    timer: [faHourglassHalf, "text-purple-700"],
    html: [faHtml5, "text-pink-700"],
    socials: [faShareNodes, "text-fuchsia-700"],
    social: [faShareNodes, "text-fuchsia-700"],
};

export interface MailSocial {
    label: string;
    value: string;
    icon: string;
    domains: string[];
}

export const useMailStore = defineStore("mail-store", () => {
    // Dependencies
    const api = useApi();
    const dayjs = useDayjs();
    const { user } = useUser();

    // States
    const mail = shallowRef<MailTemplate>(EMPTY_MAIL);
    const initMail = shallowRef<MailTemplate>(EMPTY_MAIL);
    const builderConfig = ref<MailBuilderConfig>({
        showValues: false,
        previewMinimized: false,
        codeEditorVisible: false,
        previewSize: "desktop",
    });
    const editor = ref<Editor | null>(null);

    // Individual states
    const brand = shallowRef<MailTemplate["brand"]>(EMPTY_MAIL["brand"]);
    const locale = shallowRef<MailTemplate["locale"]>(EMPTY_MAIL["locale"]);
    const form = shallowRef<MailTemplate["form"]>(EMPTY_MAIL["form"]);
    const translations = shallowRef<MailTemplate["translations"]>(
        EMPTY_MAIL["translations"],
    );
    const formField = shallowRef<MailTemplate["form_field"]>(
        EMPTY_MAIL["form_field"],
    );
    const metadata = shallowRef<MailTemplate["metadata"]>(
        EMPTY_MAIL["metadata"],
    );
    const subject = shallowRef<MailTemplate["subject"]>(EMPTY_MAIL["subject"]);
    const name = shallowRef<MailTemplate["name"]>(EMPTY_MAIL["name"]);
    const autoTranslate = shallowRef<MailTemplate["auto_translate"]>(
        EMPTY_MAIL["auto_translate"],
    );
    const description = shallowRef<MailTemplate["description"]>(
        EMPTY_MAIL["description"],
    );
    const textData = shallowRef<MailTemplate["text_data"]>(
        EMPTY_MAIL["text_data"],
    );
    const jsonData = shallowRef<MailTemplate["json_data"]>(
        EMPTY_MAIL["json_data"],
    );

    // Trackers
    const focusedJsonId = shallowRef<string>("");
    const blockUpdate = ref<boolean>(false);
    const editingTranslation = ref<MailTemplateTranslation | null>(null);

    // Statuses
    const loading = shallowRef<boolean | null>(null);
    const blockLoad = shallowRef<boolean>(false);
    const saving = shallowRef<boolean>(false);
    const savingTranslation = shallowRef<boolean>(false);
    const error = shallowRef<ApiError | null>(null);
    const lastUpdated = shallowRef<string>(new Date().toISOString());
    const socials = shallowRef<MailSocial[]>([]);

    // Data
    const { data: default_fields, refresh: refreshFields } = useAsyncData(
        async () => {
            if (!mail.value.id) {
                return [];
            }

            try {
                const data = await api.getFieldCollection(
                    "mail-template",
                    "default",
                );

                return data.data;
            } catch {
                return [];
            }
        },
        {
            immediate: false,
        },
    );

    // Computes
    const builderFields = computed(() => {
        let fields = createFieldCollection(
            (metadata.value.fields ?? []).map((field) => {
                return {
                    ...field,
                    icon: faSparkles,
                    metadata: {
                        ...field.metadata,
                        is_template_field: true,
                    },
                };
            }),
        ).add(...(default_fields.value ?? []));

        if (brand.value?.id) {
            fields = fields.withData(
                brand?.value.value_map
                    ? brand?.value.value_map
                    : {
                          brand: brand.value,
                      },
            );
        } else if (user.value?.account?.brand?.id) {
            fields = fields.withData(
                user.value.account.brand.value_map
                    ? user.value.account.brand.value_map
                    : {
                          brand: user.value.account.brand,
                      },
            );
        }

        return fields.items.map((field) => {
            if (field.key === "brand.website" && !field.default_value) {
                field.default_value = fields.replaceContent(
                    "account.brand.website",
                );
            }
            return field;
        });
    });

    const previewMail = computed(() => {
        return {
            ...useCloneDeep(mail.value),
            brand: brand.value,
            form: form.value,
            locale: locale.value,
            formField: formField.value,
            translations: translations.value,
            metadata: metadata.value,
            subject: subject.value,
            name: name.value,
            description: description.value,
            textData: textData.value,
            jsonData: jsonData.value,
        };
    });

    const fieldCollection = computed(() => {
        return createFieldCollection(builderFields.value);
    });

    const formFieldMap = computedAsync(async () => {
        return form.value
            ? await makeAndLoadFormFieldMap(form.value as Form)
            : [];
    });

    const recall = computed(() =>
        makeFormRecallMention(
            form.value?.id ? (form.value as Form) : undefined,
            [...builderFields.value, ...(formFieldMap.value ?? [])],
        ),
    );

    const lastUpdatedDisplay = computed(() => {
        return dayjs(lastUpdated.value).fromNow();
    });

    const lastUpdatedAtDisplay = useShallowComputed(() => {
        return dayjs(mail.value.updated_at).fromNow();
    });

    const breadcrumbs = computed(() => {
        const breadcrumbs = [];
        let { block, chain } =
            !focusedJsonId.value || typeof focusedJsonId.value !== "string"
                ? { block: jsonData.value, chain: [] }
                : getBlockById(focusedJsonId.value);

        if (!block) {
            block = jsonData.value;
        }

        chain = [...chain, block as JSONContent];

        for (const item of chain) {
            if (!item || item.type === "column") continue;

            breadcrumbs.push({
                label: item.type,
                block: item,
                icon: mailBlockTypeIcons[
                    item.type as keyof typeof mailBlockTypeIcons
                ],
                isActive: focusedJsonId.value === item.attrs?.id,
                onClick: () => {
                    focusedJsonId.value = item.attrs?.id ?? "";
                },
            });
        }

        return breadcrumbs;
    });

    const currentBlock = computed({
        get: () =>
            focusedJsonId.value
                ? getBlockById(focusedJsonId.value).block
                : jsonData.value,
        set: (value: JSONContent | null) => {
            if (!value) return;

            replaceBlockById(focusedJsonId.value, value);
        },
    });

    const translateables = computed(() => {
        let translateables: Record<string, string | object | undefined | null> =
            {};

        if (jsonData.value) {
            function getBlockTranslations(
                block: JSONContent,
            ): Record<string, string | object | undefined | null> {
                let translations: Record<
                    string,
                    string | object | undefined | null
                > = {};
                switch (block.type as keyof typeof mailBlockTypeIcons) {
                    case "document":
                    case "signature":
                    case "section":
                    case "layout":
                    case "column":
                    case "button-group":
                        for (const child of block.content ?? []) {
                            translations = {
                                ...translations,
                                ...getBlockTranslations(child),
                            };
                        }
                        break;
                    case "paragraph":
                    case "button":
                    case "heading":
                    case "blockquote":
                    case "number-list":
                    case "bullet-list":
                        return {
                            [`${block.attrs?.id}.content`]: useCloneDeep(
                                block.content,
                            ),
                        };
                    case "form":
                        return {
                            [`${block.attrs?.id}.ratingLabelMax`]:
                                block.attrs?.ratingLabelMax,
                            [`${block.attrs?.id}.ratingLabelMid`]:
                                block.attrs?.ratingLabelMid,
                            [`${block.attrs?.id}.ratingLabelMin`]:
                                block.attrs?.ratingLabelMin,
                            [`${block.attrs?.id}.content`]: useCloneDeep(
                                block.content,
                            ),
                        };
                    // case "image":
                    //     break;
                    // case "gallery":
                    //     break;
                    // case "video":
                    //     break;
                    // case "timer":
                    //     break;
                    // case "html":
                    //     break;
                    default:
                        return {};
                }

                return translations;
            }

            translateables = {
                ...translateables,
                ...getBlockTranslations(jsonData.value),
            };
        }

        return translateables;
    });

    const utmCampaign = computed({
        get: () => metadata.value.utm_campaign,
        set: (value: MailTemplate["metadata"]["utm_campaign"]) => {
            metadata.value = {
                ...metadata.value,
                utm_campaign: value,
            };
        },
    });

    // Actions
    async function loadMailById(
        id: string,
    ): Promise<ApiResponse<MailTemplate> | ApiError | null> {
        if (blockLoad.value) {
            blockLoad.value = false;
            return null;
        }

        if (loading.value) {
            return null;
        }

        loading.value = true;

        try {
            try {
                const response = await api.mail_templates.show(id, {
                    value_map: true,
                });
                mail.value = response.data;
                builderConfig.value = {
                    showValues: mail.value.type === "html",
                    previewMinimized: false,
                    codeEditorVisible: mail.value.type === "html",
                    previewSize: "desktop",
                };
                socials.value = response.extra.socials ?? [];
                await refreshFields();
                return response;
            } catch (err) {
                error.value = err as ApiError;
                return err as ApiError;
            }
        } finally {
            loading.value = false;
        }
    }

    async function saveMail(): Promise<
        ApiResponse<MailTemplate> | ApiError | null
    > {
        if (saving.value) {
            return null;
        }

        saving.value = true;

        try {
            const response = await api.mail_templates.update(
                mail.value.id,
                mail.value,
            );

            if (!response.data.form?.id) {
                form.value = { id: null };
                formField.value = { id: null };
            }

            mail.value.updated_at = response.data.updated_at;
            initMail.value = useCloneDeep(mail.value);
            lastUpdated.value = new Date().toISOString();
            triggerRef(lastUpdatedAtDisplay);
            await refreshFields();
            return response;
        } catch (err) {
            error.value = err as ApiError;
            return err as ApiError;
        } finally {
            saving.value = false;
        }
    }

    async function saveTranslation(): Promise<
        ApiResponse<MailTemplateTranslation> | ApiError | null
    > {
        if (savingTranslation.value) {
            return null;
        }

        savingTranslation.value = true;

        try {
            const response = await api.mail_templates.updateTranslation(
                mail.value.id,
                editingTranslation.value ?? {},
            );

            const index =
                translations.value?.findIndex(
                    (t) => t.id === response.data?.id,
                ) ?? -1;

            if (index !== -1) {
                translations.value![index] = response.data;
            } else {
                translations.value = [
                    ...(translations.value ?? []),
                    response.data,
                ].sort((a, b) => a.locale.name.localeCompare(b.locale.name));
            }

            editingTranslation.value = null;

            return response;
        } catch (err) {
            error.value = err as ApiError;
            return err as ApiError;
        } finally {
            savingTranslation.value = false;
        }
    }

    async function deleteTranslation(id: number) {
        const toast = useToast();
        toast.add({
            id: `delete-translation-${id}`,
            title: "Delete translation?",
            description: "This action cannot be undone.",
            icon: "i-heroicons-exclamation-triangle",
            color: "red",
            timeout: 0,
            actions: [
                {
                    label: "Delete",
                    color: "red",
                    click: async () => {
                        await api.mail_templates.deleteTranslation(
                            mail.value.id,
                            id,
                        );
                        editingTranslation.value = null;
                        translations.value =
                            translations.value?.filter((t) => t.id !== id) ??
                            [];
                        toast.remove(`delete-translation-${id}`);
                    },
                },
                {
                    label: "Cancel",
                    click: () => {
                        toast.remove(`delete-translation-${id}`);
                    },
                },
            ],
        });
    }

    async function sendTestEmail(id: string, options: TestMailOptions = {}) {
        return await api
            .request(
                api.route(
                    "api.mail-templates.test-mail",
                    "mail-templates/{id}/test",
                    { id },
                ),
                {
                    method: "POST",
                    body: options,
                },
            )
            .then((data) => {
                useToast().add({
                    title: "Test mail scheduled!",
                    description: "It will be sent shortly.",
                    icon: "i-heroicons-check",
                });

                return data;
            });
    }

    function editMail(item: MailTemplate) {
        mail.value = item;
        initMail.value = useCloneDeep(item);
        blockLoad.value = true;

        navigateTo({
            name: "emails-id",
            params: {
                id: item.id,
            },
        });
    }

    function hasChanges(): boolean {
        const currentMail = useCloneDeep(mail.value);
        delete currentMail.translations;
        const currentInitMail = useCloneDeep(initMail.value);
        delete currentInitMail.translations;
        return !isEqual(currentMail, currentInitMail);
    }

    function onChange(callback?: () => void) {
        if (callback) callback();
        lastUpdated.value = new Date().toISOString();
    }

    function getBlockById(
        id: string,
        content: JSONContent | JSONContent[] = [],
        chain: JSONContent[] = [],
    ): { block: JSONContent | null; chain: JSONContent[] } {
        if (!jsonData.value) {
            return { block: null, chain };
        }

        if (!Array.isArray(content)) {
            content = [content];
        }

        if (chain.length === 0 && content.length === 0) {
            content = [jsonData.value];
        }

        if (!id) {
            return { block: jsonData.value, chain };
        }

        for (const block of content as JSONContent[]) {
            if (block.attrs?.id === id) {
                return { block, chain };
            }

            if (block.content) {
                const result = getBlockById(id, block.content, [
                    ...chain,
                    block,
                ]);

                if (result.block) {
                    return result;
                }
            }
        }

        return { block: null, chain };
    }

    function replaceBlockById(id: string, block: JSONContent) {
        if (!jsonData.value || !id) {
            return;
        }

        if (id === jsonData.value.attrs?.id) {
            jsonData.value = block;
            return;
        }

        function replaceContent(content: JSONContent[]) {
            for (const index in content) {
                if (content[index].attrs?.id === id) {
                    content[index] = block;
                    return [content, true];
                }

                if (content[index].content) {
                    const [newContent, found] = replaceContent(
                        content[index].content,
                    );

                    content[index].content = newContent as JSONContent[];

                    if (found) {
                        return [content, true];
                    }
                }
            }

            return [content, false];
        }

        jsonData.value = {
            ...jsonData.value,
            content: replaceContent(
                jsonData.value?.content ?? [],
            )[0] as JSONContent[],
        };
    }

    function getParentBlock(id: string): JSONContent | null {
        if (!jsonData.value) {
            return null;
        }

        const { chain } = getBlockById(id, jsonData.value);

        return chain.length > 0 ? chain[chain.length - 1] : null;
    }

    function getThemeForId(id: string) {
        const { chain } = getBlockById(id);

        let theme: Record<string, any> = {};
        for (const block of chain) {
            theme = useMerge(theme, useCloneDeep(block.attrs?.theme ?? {}));
        }

        return theme;
    }

    function getSpacingBeforeId(id: string) {
        const spacing = {
            marginBottom: 0,
            marginTop: 0,
            paddingBottom: 0,
            paddingLeft: 0,
            paddingRight: 0,
            paddingTop: 0,
        };

        const { chain } = getBlockById(id);

        for (const block of chain) {
            spacing.marginBottom +=
                parseInt(block.attrs?.marginBottom ?? 0) ?? 0;
            spacing.marginTop += parseInt(block.attrs?.marginTop ?? 0) ?? 0;
            spacing.paddingBottom +=
                parseInt(block.attrs?.paddingBottom ?? 0) ?? 0;
            spacing.paddingLeft += parseInt(block.attrs?.paddingLeft ?? 0) ?? 0;
            spacing.paddingRight +=
                parseInt(block.attrs?.paddingRight ?? 0) ?? 0;
            spacing.paddingTop += parseInt(block.attrs?.paddingTop ?? 0) ?? 0;
        }

        return spacing;
    }

    function setBlockById(
        id: string,
        attrs: Record<string, any>,
        content?: JSONContent[],
        marks?: JSONContent["marks"],
    ) {
        const { block } = getBlockById(id);

        if (!block) {
            return;
        }

        block.attrs = useMerge(block.attrs, { ...attrs, id: block.attrs?.id });

        if (content) {
            block.content = content;
        }

        if (marks) {
            block.marks = marks;
        }
    }

    function findMatchingSocial(src: string): MailSocial | undefined {
        if (!src) {
            return undefined;
        }

        const social = socials.value.find(
            (social) => social.value === src || social.label === src,
        );

        if (social) {
            return social;
        }

        return socials.value.find(
            (social) =>
                social.domains.filter((domain) => src.includes(domain)).length >
                0,
        );
    }

    // Watchers
    watch(mail, (to) => {
        initMail.value = useCloneDeep(to);
        brand.value = to.brand;
        form.value = to.form;
        locale.value = to.locale;
        formField.value = to.form_field;
        translations.value = to.translations;
        metadata.value = to.metadata;
        subject.value = to.subject;
        name.value = to.name;
        autoTranslate.value = to.auto_translate;
        description.value = to.description;
        textData.value = to.text_data;
        jsonData.value = to.json_data;
        onChange();
    });

    watch(brand, (to) => onChange(() => (mail.value.brand = to)));
    watch(form, (to) => onChange(() => (mail.value.form = to)));
    watch(formField, (to) => onChange(() => (mail.value.form_field = to)));
    watch(translations, (to) => onChange(() => (mail.value.translations = to)));
    watch(locale, (to) => onChange(() => (mail.value.locale = to)));
    watch(metadata, (to) => onChange(() => (mail.value.metadata = to)));
    watch(subject, (to) => onChange(() => (mail.value.subject = to)));
    watch(name, (to) => onChange(() => (mail.value.name = to)));
    watch(autoTranslate, (to) =>
        onChange(() => (mail.value.auto_translate = to)),
    );
    watch(description, (to) => onChange(() => (mail.value.description = to)));
    watch(textData, (to) => onChange(() => (mail.value.text_data = to)));
    watch(jsonData, (to) => onChange(() => (mail.value.json_data = to)));

    return {
        // States
        mail,
        builderConfig,
        editor,

        // Individual states
        brand,
        form,
        formField,
        metadata,
        translations,
        subject,
        name,
        autoTranslate,
        description,
        locale,
        textData,
        jsonData,
        socials,
        // Trackers
        focusedJsonId,
        editingTranslation,
        blockUpdate,

        // Statuses
        loading,
        saving,
        error,
        savingTranslation,

        // Readables
        lastUpdatedDisplay,
        lastUpdatedAtDisplay,
        translateables,

        // Computes
        builderFields,
        fieldCollection,
        formFieldMap,
        recall,
        breadcrumbs,
        currentBlock,
        previewMail,
        utmCampaign,

        // Actions
        loadMailById,
        editMail,
        saveMail,
        sendTestEmail,
        hasChanges,
        getBlockById,
        getParentBlock,
        getThemeForId,
        refreshFields,
        getSpacingBeforeId,
        setBlockById,
        saveTranslation,
        deleteTranslation,
        replaceBlockById,
        findMatchingSocial,
    };
});
